import { useState, useEffect, useRef, useCallback } from "react";
import { io } from "socket.io-client";
import { Monitor, MonitorOff, AlertTriangle, Clock, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";

const STATUS_CONFIG = {
  pending: { color: "bg-yellow-500", icon: Clock, label: "En attente" },
  sharing: { color: "bg-green-500", icon: Monitor, label: "Écran partagé" },
  stopped: { color: "bg-red-500", icon: MonitorOff, label: "Partage arrêté" },
  refused: { color: "bg-gray-500", icon: X, label: "Refusé" },
  error: { color: "bg-orange-500", icon: AlertTriangle, label: "Erreur" },
};

/**
 * ScreenShareProvider — renders a consent panel + persistent indicator
 * for candidates whose screen needs to be shared to trainers.
 * 
 * Props:
 *   meetingId  - ID of the current meeting
 *   userId     - current user's ID
 *   userName   - current user's display name
 */
export default function ScreenShareProvider({ meetingId, userId, userName }) {
  const [status, setStatus] = useState("pending"); // pending | sharing | stopped | refused | error
  const [elapsed, setElapsed] = useState(0);
  const [dismissed, setDismissed] = useState(false);
  const [errorDetail, setErrorDetail] = useState("");

  const socketRef = useRef(null);
  const pcRef = useRef(null);
  const streamRef = useRef(null);
  const timerRef = useRef(null);
  const iceCandidateBuffer = useRef([]);
  const remoteDescSet = useRef(false);

  // Initialize Socket.IO + join room
  useEffect(() => {
    const socket = io(window.location.origin, {
      path: "/ws/socket.io",
      transports: ["websocket", "polling"],
    });

    socket.on("connect", () => {
      socket.emit("screen:join", {
        meeting_id: meetingId,
        user_id: userId,
        user_name: userName,
        role: "candidate",
      });
    });

    // When monitor sends an answer to our offer
    socket.on("screen:answer", async (data) => {
      try {
        if (pcRef.current && data.sdp) {
          await pcRef.current.setRemoteDescription(new RTCSessionDescription(data.sdp));
          remoteDescSet.current = true;
          // Flush buffered ICE candidates
          for (const c of iceCandidateBuffer.current) {
            await pcRef.current.addIceCandidate(new RTCIceCandidate(c));
          }
          iceCandidateBuffer.current = [];
        }
      } catch (err) {
        console.error("Error setting remote description:", err);
      }
    });

    // When monitor sends ICE candidate
    socket.on("screen:ice", async (data) => {
      try {
        if (pcRef.current && data.candidate) {
          if (remoteDescSet.current) {
            await pcRef.current.addIceCandidate(new RTCIceCandidate(data.candidate));
          } else {
            // Buffer until remote description is set
            iceCandidateBuffer.current.push(data.candidate);
          }
        }
      } catch (err) {
        console.error("Error adding ICE candidate:", err);
      }
    });

    // When a monitor joins late, re-send our offer if we're already sharing
    socket.on("screen:request-offer", async () => {
      if (pcRef.current && streamRef.current) {
        try {
          // Close old PC and create fresh one
          pcRef.current.close();

          const pc = new RTCPeerConnection({
            iceServers: [
              { urls: "stun:stun.l.google.com:19302" },
              { urls: "stun:stun1.l.google.com:19302" },
              { urls: "turn:openrelay.metered.ca:80", username: "openrelayproject", credential: "openrelayproject" },
              { urls: "turn:openrelay.metered.ca:443", username: "openrelayproject", credential: "openrelayproject" },
              { urls: "turn:openrelay.metered.ca:443?transport=tcp", username: "openrelayproject", credential: "openrelayproject" },
            ],
          });

          remoteDescSet.current = false;
          iceCandidateBuffer.current = [];

          streamRef.current.getTracks().forEach((track) => {
            pc.addTrack(track, streamRef.current);
          });

          pc.onicecandidate = (e) => {
            if (e.candidate && socketRef.current) {
              socketRef.current.emit("screen:ice", {
                user_id: userId,
                candidate: e.candidate.toJSON(),
              });
            }
          };

          const offer = await pc.createOffer();
          await pc.setLocalDescription(offer);

          socket.emit("screen:offer", {
            sdp: { type: pc.localDescription.type, sdp: pc.localDescription.sdp },
          });

          pcRef.current = pc;
        } catch (err) {
          console.error("Error re-sending offer:", err);
        }
      }
    });

    socketRef.current = socket;

    return () => {
      stopSharing();
      socket.disconnect();
    };
  }, [meetingId, userId, userName]);

  // Elapsed timer when sharing
  useEffect(() => {
    if (status === "sharing") {
      const start = Date.now();
      timerRef.current = setInterval(() => {
        setElapsed(Math.floor((Date.now() - start) / 1000));
      }, 1000);
    } else {
      clearInterval(timerRef.current);
      setElapsed(0);
    }
    return () => clearInterval(timerRef.current);
  }, [status]);

  const startSharing = useCallback(async () => {
    // Check if getDisplayMedia is available (requires HTTPS)
    if (!navigator.mediaDevices?.getDisplayMedia) {
      setStatus("error");
      setErrorDetail("getDisplayMedia non disponible — HTTPS requis ou navigateur incompatible");
      if (socketRef.current) {
        socketRef.current.emit("screen:status", { status: "error" });
      }
      toast.error("Le partage d'écran nécessite une connexion HTTPS sécurisée");
      return;
    }

    try {
      // Request screen share
      const stream = await navigator.mediaDevices.getDisplayMedia({
        video: {
          frameRate: { ideal: 5, max: 10 },
          width: { ideal: 1280, max: 1920 },
          height: { ideal: 720, max: 1080 },
        },
        audio: false,
      });

      streamRef.current = stream;

      // Detect when user stops sharing via browser UI
      stream.getVideoTracks()[0].onended = () => {
        stopSharing();
      };

      // Create WebRTC peer connection with STUN + TURN
      const pc = new RTCPeerConnection({
        iceServers: [
          { urls: "stun:stun.l.google.com:19302" },
          { urls: "stun:stun1.l.google.com:19302" },
          {
            urls: "turn:openrelay.metered.ca:80",
            username: "openrelayproject",
            credential: "openrelayproject",
          },
          {
            urls: "turn:openrelay.metered.ca:443",
            username: "openrelayproject",
            credential: "openrelayproject",
          },
          {
            urls: "turn:openrelay.metered.ca:443?transport=tcp",
            username: "openrelayproject",
            credential: "openrelayproject",
          },
        ],
      });

      // Reset state for new connection
      remoteDescSet.current = false;
      iceCandidateBuffer.current = [];

      // Add screen track
      stream.getTracks().forEach((track) => {
        pc.addTrack(track, stream);
      });

      // Send ICE candidates to server
      pc.onicecandidate = (e) => {
        if (e.candidate && socketRef.current) {
          socketRef.current.emit("screen:ice", {
            user_id: userId,
            candidate: e.candidate.toJSON(),
          });
        }
      };

      // Create and send offer
      const offer = await pc.createOffer();
      await pc.setLocalDescription(offer);

      if (socketRef.current) {
        socketRef.current.emit("screen:offer", {
          sdp: { type: pc.localDescription.type, sdp: pc.localDescription.sdp },
        });
        socketRef.current.emit("screen:status", { status: "sharing" });
      }

      pcRef.current = pc;
      setStatus("sharing");
      toast.success("Écran partagé avec succès");
    } catch (err) {
      console.error("Screen share error:", err);
      const detail = `${err.name}: ${err.message}`;
      setErrorDetail(detail);
      if (err.name === "NotAllowedError" || err.name === "AbortError") {
        setStatus("refused");
        if (socketRef.current) {
          socketRef.current.emit("screen:status", { status: "refused" });
        }
        toast.error("Partage d'écran refusé — cliquez sur Réessayer quand vous êtes prêt");
      } else {
        setStatus("error");
        if (socketRef.current) {
          socketRef.current.emit("screen:status", { status: "error" });
        }
        toast.error(`Erreur : ${err.message || "Impossible de partager l'écran"}`);
      }
    }
  }, [meetingId, userId]);

  const stopSharing = useCallback(() => {
    // Stop media tracks
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
    }
    // Close peer connection
    if (pcRef.current) {
      pcRef.current.close();
      pcRef.current = null;
    }
    // Notify server
    if (socketRef.current?.connected) {
      socketRef.current.emit("screen:status", { status: "stopped" });
    }
    setStatus("stopped");
  }, []);

  const formatTime = (s) => {
    const m = Math.floor(s / 60);
    const sec = s % 60;
    return `${m.toString().padStart(2, "0")}:${sec.toString().padStart(2, "0")}`;
  };

  const cfg = STATUS_CONFIG[status] || STATUS_CONFIG.pending;
  const StatusIcon = cfg.icon;

  // Consent panel (shown when pending or refused/error and not dismissed)
  if (status === "pending" || ((status === "refused" || status === "error") && !dismissed)) {
    return (
      <div className="border rounded-xl p-6 bg-card shadow-lg max-w-md mx-auto my-6 animate-fade-in">
        <div className="flex items-start gap-4">
          <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-primary/10 shrink-0">
            <Monitor className="h-6 w-6 text-primary" />
          </div>
          <div className="flex-1">
            <h3 className="font-semibold text-lg">Partage d'écran requis</h3>
            <p className="text-sm text-muted-foreground mt-1">
              Cette session est surveillée. Votre écran sera visible par le formateur pendant toute la durée de l'examen/TP.
            </p>
            {status === "refused" && (
              <div className="mt-3 p-3 bg-destructive/10 rounded-lg text-sm text-destructive">
                Vous avez refusé le partage. Le formateur est notifié. Vous pouvez réessayer.
                {errorDetail && (
                  <div className="mt-1 text-xs font-mono opacity-70 break-all">
                    Détail : {errorDetail}
                  </div>
                )}
              </div>
            )}
            {status === "error" && (
              <div className="mt-3 p-3 bg-orange-100 dark:bg-orange-900/20 rounded-lg text-sm text-orange-700 dark:text-orange-300">
                Erreur technique. Vérifiez que votre navigateur autorise le partage d'écran.
                {errorDetail && (
                  <div className="mt-1 text-xs font-mono opacity-70 break-all">
                    Détail : {errorDetail}
                  </div>
                )}
              </div>
            )}
            <div className="flex gap-2 mt-4">
              <Button onClick={startSharing} className="gap-2">
                <Monitor className="h-4 w-4" />
                {status === "pending" ? "Partager mon écran" : "Réessayer"}
              </Button>
              {(status === "refused" || status === "error") && (
                <Button variant="ghost" onClick={() => setDismissed(true)}>
                  Ignorer
                </Button>
              )}
            </div>
            <p className="text-xs text-muted-foreground mt-3">
              ℹ️ Vous pourrez arrêter le partage à tout moment. Le formateur sera alerté si le partage est interrompu.
            </p>
          </div>
        </div>
      </div>
    );
  }

  // Persistent indicator when sharing or stopped
  if (status === "sharing" || status === "stopped") {
    return (
      <div
        className={`flex items-center gap-3 px-4 py-2 rounded-lg text-white text-sm ${
          status === "sharing" ? "bg-green-600" : "bg-red-600"
        }`}
      >
        <span className={`h-2.5 w-2.5 rounded-full ${status === "sharing" ? "bg-white animate-pulse" : "bg-white/50"}`} />
        <StatusIcon className="h-4 w-4" />
        <span className="font-medium">{cfg.label}</span>
        {status === "sharing" && <span className="opacity-80">• {formatTime(elapsed)}</span>}
        {status === "sharing" ? (
          <Button
            variant="ghost"
            size="sm"
            className="ml-auto text-white hover:text-white hover:bg-white/20 h-7 px-2"
            onClick={stopSharing}
          >
            Arrêter
          </Button>
        ) : (
          <Button
            variant="ghost"
            size="sm"
            className="ml-auto text-white hover:text-white hover:bg-white/20 h-7 px-2"
            onClick={startSharing}
          >
            Reprendre
          </Button>
        )}
      </div>
    );
  }

  return null;
}
